/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {requestDemo} from '../core/_requests'
import {Link, useNavigate} from 'react-router-dom'

const initialValues = {
  name: '',
  address: '',
  phone: '',
}

const requestDemoSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(100, 'Maximum 100 symbols')
    .required('Name is required'),
  address: Yup.string()
    .min(5, 'Minimum 5 symbols')
    .max(200, 'Maximum 200 symbols')
    .required('Address is required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
})

export function RequestDemo() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [countdown, setCountdown] = useState(20)
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema: requestDemoSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        await requestDemo(values.name, values.address, values.phone)
        setSuccess(true)
        setLoading(false)

        // Start countdown
        let timeLeft = 20
        const countdownInterval = setInterval(() => {
          timeLeft -= 1
          setCountdown(timeLeft)
          if (timeLeft <= 0) {
            clearInterval(countdownInterval)
            navigate('/auth/login')
          }
        }, 1000)
      } catch (error) {
        console.error(error)
        setStatus('Failed to submit demo request. Please try again.')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_request_demo_form'
      onSubmit={formik.handleSubmit}
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Request Demo</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Fill in your details and we'll get in touch with you shortly
        </div>
      </div>
      {/* end::Heading */}

      {success && (
        <div className='mb-lg-15 alert alert-success'>
          <div className='d-flex align-items-center'>
            <i className='ki-duotone ki-check-circle fs-2x text-success me-4'>
              <span className='path1'></span>
              <span className='path2'></span>
            </i>
            <div className='flex-grow-1'>
              <h4 className='alert-heading fw-bold mb-2'>Thank you for your interest!</h4>
              <p className='mb-2'>
                Your demo request has been submitted successfully. Our team will review your information and contact you shortly to schedule a personalized demo of our VAS Revenue Intelligence platform.
              </p>
              <div className='separator separator-dashed border-success my-3'></div>
              <p className='mb-0 fw-semibold'>
                <i className='ki-duotone ki-timer fs-5 me-2'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                  <span className='path3'></span>
                </i>
                Redirecting to login page in <span className='badge badge-light-success fs-6'>{countdown}</span> seconds...
              </p>
            </div>
          </div>
        </div>
      )}

      {formik.status && !success && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      {!success && (
        <>
          {/* begin::Form group Name */}
          <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-dark fs-6'>Full Name</label>
        <input
          placeholder='Enter your full name'
          type='text'
          autoComplete='off'
          {...formik.getFieldProps('name')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.name && formik.errors.name,
            },
            {
              'is-valid': formik.touched.name && !formik.errors.name,
            }
          )}
        />
        {formik.touched.name && formik.errors.name && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.name}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Address */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-dark fs-6'>Address</label>
        <textarea
          placeholder='Enter your address'
          autoComplete='off'
          rows={3}
          {...formik.getFieldProps('address')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.address && formik.errors.address,
            },
            {
              'is-valid': formik.touched.address && !formik.errors.address,
            }
          )}
        />
        {formik.touched.address && formik.errors.address && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.address}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Phone */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-dark fs-6'>Phone Number</label>
        <input
          placeholder='Enter 10 digit phone number'
          type='text'
          autoComplete='off'
          {...formik.getFieldProps('phone')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.phone && formik.errors.phone,
            },
            {
              'is-valid': formik.touched.phone && !formik.errors.phone,
            }
          )}
        />
        {formik.touched.phone && formik.errors.phone && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.phone}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='text-center'>
        <button
          type='submit'
          id='kt_request_demo_submit'
          className='btn btn-lg btn-primary w-100 mb-5'
          disabled={formik.isSubmitting || !formik.isValid || success}
        >
          {!loading && <span className='indicator-label'>Submit Request</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            className='btn btn-lg btn-light-primary w-100 mb-5'
          >
            Back to Login
          </button>
        </Link>
      </div>
      {/* end::Form group */}
        </>
      )}
    </form>
  )
}
